import { Button, FormControl, FormLabel, Input, Select, Spacer, Stack, Tag, Text } from "@chakra-ui/react";
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { AuthContext } from "contexts/AuthContext";
import { useBroadcast } from "hooks/useBroadcast";
import { useContent } from "hooks/useContent";
import FinalPlaylist from "components/broadcastSelection/FinalPlaylist";
import { isCompositeBroadcast } from "lib/playlist";
import { formatSeconds, tokenFilter } from "lib/utils";
import { sumDurations } from "lib/broadcast";
import { compareValues } from "lib/sort";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AvailableBroadcastItem, SelectedBroadcastItem } from "components/selection/BroadcastItems";
import { countAdded, EMPTY_LIST, makeEntries, sameList, SortButton } from "components/selection/common";

export default function BroadcastSelection({ selected, onChange }) {

  const { user } = useContext(AuthContext)
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const [broadcastItems, loadingBrd, errorBrd, refreshBrd] = useBroadcast({user, includeMultiplier: true })
  const [contentItems] = useContent({user})
  const [filterTerm, setFilter] = useState('')
  const [pageSize, setPageSize] = useState(10)
  const [page, setPage] = useState(0)
  const [sortKey, setSortKey] = useState('name')

  // The playlist: one entry per position, { uid, id } (see makeEntries)
  const [entries, setEntries] = useState(() => makeEntries(selected || EMPTY_LIST))

  const itemsById = useMemo(() => new Map(broadcastItems.map(i => [i.id, i])), [broadcastItems])
  const contentById = useMemo(() => new Map(contentItems.map(i => [i.id, i])), [contentItems])

  const selectionDuration = (ids) => sumDurations(ids, id => itemsById.get(id)?.duration)

  // Follow the parent when it hands us a different list; echoes of our own changes
  // are equal by value and keep the entries (and their keys) as they are.
  useEffect(() => {
    const next = selected || EMPTY_LIST
    setEntries(current => sameList(current.map(e => e.id), next) ? current : makeEntries(next))
  }, [selected])

  // The total duration of the current selection. It changes when the list changes, and also
  // when a broadcast in it is edited (see `onSaved`): then the parent has to hear about it.
  const currentDuration = broadcastItems.length === 0 ? null : selectionDuration(entries.map(e => e.id))
  const reportedDuration = useRef(null)

  // Report the selection once the broadcasts are loaded, so the parent gets the total
  // duration right away, and again whenever that total changes without the list changing.
  useEffect(() => {
    if (currentDuration === null || reportedDuration.current === currentDuration) return
    reportedDuration.current = currentDuration
    onChange({ items: entries.map(e => e.id), duration: currentDuration })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDuration])

  const updateSelection = (list) => {
    const ids = list.map(e => e.id)
    const duration = selectionDuration(ids)
    reportedDuration.current = duration
    setEntries(list)
    onChange({
      items: ids,
      duration
    })
  }

  // A broadcast of the list was edited in its own drawer: load the fresh data, so the
  // names, durations and the total above are current (the effect above reports the total).
  const handleSaved = () => refreshBrd()

  const addToSelection = (id) => updateSelection([...entries, ...makeEntries([id], true)])

  const removeFromSelection = (index) => updateSelection(entries.filter((_, i) => i !== index))

  // A multiplier broadcast replaces the entry at the same position and keeps its key
  const replaceInSelection = (index, replacementId) => {
    updateSelection(entries.map((e, i) => i === index ? { ...e, id: replacementId } : e))
  }

  const handleDragEnd = (event) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = entries.findIndex(e => e.uid === active.id)
    const newIndex = entries.findIndex(e => e.uid === over.id)
    if (oldIndex < 0 || newIndex < 0) return

    updateSelection(arrayMove(entries, oldIndex, newIndex))
  }

  const sortBroadcasts = (a, b) => {
    const direction = sortKey.startsWith('-') ? -1 : 1
    const key = sortKey.replace('-', '')
    // Names in the Serbian alphabet, dates and numbers as numbers; empty values stay last
    const av = a[key]
    const bv = b[key]
    const missingA = av === undefined || av === null || av === ''
    const missingB = bv === undefined || bv === null || bv === ''
    if (missingA || missingB) return (missingA ? 1 : 0) - (missingB ? 1 : 0)
    return compareValues(av, bv) * direction
  }

  const setSortBroadcasts = (key) => {
    if (sortKey.includes(key)) {
      setSortKey(sortKey.startsWith('-') ? key : '-' + key)
    } else {
      setSortKey('-' + key)
    }
  }

  const counts = useMemo(() => entries.reduce((s, e) => ({ ...s, [e.id]: (s[e.id] || 0) + 1 }), {}), [entries])

  const sortedItems = broadcastItems.length === 0 ? [] : entries.map(e => ({
    ...(itemsById.get(e.id) || { id: e.id, missing: true }),
    sortKey: e.uid,
    isNew: !!e.added
  }))
  const addedCount = countAdded(entries)

  // Only REGULAR broadcasts can be picked: not composite ones and not multiplier copies
  // (a multiplier is set on the selected item instead). An EMPTY regular broadcast (no content
  // list at all in the database) can be picked too: it plays nothing and disturbs nobody.
  const regularBroadcasts = broadcastItems.filter(i => !i.sourceId && !isCompositeBroadcast(i))

  const filteredBroadcasts = regularBroadcasts
    .filter(i => tokenFilter(i.name, filterTerm))
    .sort(sortBroadcasts)

  const pageCount = Math.max(1, Math.ceil(filteredBroadcasts.length / pageSize))
  const currentPage = Math.min(page, pageCount - 1)
  const filteredItems = filteredBroadcasts.slice(currentPage * pageSize, currentPage * pageSize + pageSize)

  return (
    <>
      <FormControl>
        <Stack direction={{ base: 'column', md: 'row' }} spacing={0} gap={{ base: 6, md: 0 }} mt={4}>
          <Stack direction={'column'} spacing={2} w={{ base: '100%', md: '50%' }} pr={{ base: 0, md: 4 }}>
            <Stack direction={'row'} w='100%' wrap='wrap' gap={2} spacing={0}>
              <FormLabel>Available regular broadcasts ({filteredBroadcasts.length} / {regularBroadcasts.length})</FormLabel>
              <Spacer />
              <Stack direction={'row'} spacing={1} wrap='wrap'>
                <SortButton label='name' field='name' sortKey={sortKey} onSort={setSortBroadcasts} />
                <SortButton label='date' field='updated_at' sortKey={sortKey} onSort={setSortBroadcasts} />
                <Select
                  w='unset'
                  style={{borderRadius: '30px' }}
                  size='xs'
                  display='flex'
                  alignItems='center'
                  defaultValue='10'
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0) }}>
                  <option value='10'>10</option>
                  <option value='20'>20</option>
                  <option value='50'>50</option>
                  <option value='100'>100</option>
                  <option value='200'>200</option>
                </Select>
              </Stack>
            </Stack>
            <Input placeholder='Search...' size='sm' h={8} value={filterTerm} onChange={e => { setFilter(e.target.value); setPage(0) }} />
            <Stack direction='column' w="100%">
              {filteredItems.map(item => (
                <AvailableBroadcastItem key={item.id} item={item} count={counts[item.id] || 0} onAdd={addToSelection} onSaved={handleSaved} />
              ))}
            </Stack>
            <Stack direction='row' justify='center' align='center' spacing={3} pt={2}>
              <Button size='xs' variant='ghost' colorScheme='brand' onClick={() => setPage(p => Math.max(0, p - 1))} isDisabled={currentPage <= 0}>Previous</Button>
              <Text fontSize='sm' color='gray'>Page {currentPage + 1} / {pageCount}</Text>
              <Button size='xs' variant='ghost' colorScheme='brand' onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))} isDisabled={currentPage >= pageCount - 1}>Next</Button>
            </Stack>
          </Stack>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <Stack direction={'column'} spacing={2} w={{ base: '100%', md: '50%' }} pl={{ base: 0, md: 4 }}>
              <FormLabel>Composite playlist</FormLabel>
              <FinalPlaylist ids={entries.map(e => e.id)} broadcastsById={itemsById} contentById={contentById} />
              <Stack direction={'row'} alignItems='center' minH={8} wrap='wrap' gap={2} spacing={0}>
                <Text>Total duration</Text>
                <Tag px={2} variant='subtle' colorScheme='cyan'>
                  {formatSeconds(sortedItems.reduce((s, i) => s + (i.duration || 0), 0))}
                </Tag>
                <Tag px={2} variant='subtle' colorScheme='gray'>{sortedItems.length} items</Tag>
                {addedCount > 0 && <Tag px={2} variant='subtle' colorScheme='brand' title='Highlighted below until the broadcast is saved'>{addedCount} added, not saved</Tag>}
              </Stack>
              <SortableContext
                items={sortedItems.map(i => i.sortKey)}
                strategy={verticalListSortingStrategy}
              >
                {sortedItems.map((item, index) => (
                  <SelectedBroadcastItem
                    key={item.sortKey}
                    item={item}
                    source={item.sourceId ? itemsById.get(item.sourceId) : undefined}
                    position={index + 1}
                    onRemove={removeFromSelection}
                    onReplace={replaceInSelection}
                    onRefresh={refreshBrd}
                    onSaved={handleSaved}
                  />
                ))}
              </SortableContext>
            </Stack>
          </DndContext>
        </Stack>
      </FormControl>
    </>
  );
}
